import { useEffect } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ColecaoDisponivel,
  ContaSeguida,
  ModoSincronizacaoReferencias,
} from "@base/contracts";
import { useReferenciasGateway } from "./referencias.contexto";
import type { FiltroDeReferencias, FiltroDeStories } from "../infrastructure/referencias.gateway";
import {
  gravarCacheColecoesDisponiveis,
  lerCacheColecoesDisponiveis,
  limparCacheColecoesDisponiveis,
} from "../infrastructure/cache-colecoes-disponiveis";

/** Teto da API por página; o hook busca todas as páginas automaticamente. */
export const LIMITE_POR_PAGINA = 200;

export const chavesDeReferencias = {
  todas: ["referencias"] as const,
  lista: (filtro: Record<string, unknown>) =>
    ["referencias", "lista", filtro] as const,
  colecoes: ["referencias", "colecoes"] as const,
  disponiveis: ["referencias", "colecoes", "disponiveis"] as const,
  contasSeguidas: ["referencias", "instagram", "seguindo"] as const,
  stories: (filtro: Record<string, unknown>) =>
    ["referencias", "stories", "lista", filtro] as const,
  storiesContas: ["referencias", "stories", "contas"] as const,
  storiesSincronizacao: ["referencias", "stories", "sincronizacao"] as const,
  sincronizacao: ["referencias", "sincronizacao"] as const,
  instagramSessao: ["referencias", "instagram", "sessao"] as const,
  integracoes: ["referencias", "integracoes"] as const,
  comentarios: (postId: string) =>
    ["referencias", "comentarios", postId] as const,
};

export function useListarReferencias(filtro: FiltroDeReferencias = {}) {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.lista(filtro),
    queryFn: () => gateway.listar(filtro),
  });
}

/**
 * Carrega a lista inteira do filtro (todas as páginas), sem pedir "carregar mais".
 */
export function useListarReferenciasInfinito(
  filtro: Omit<FiltroDeReferencias, "cursor" | "limite"> = {},
) {
  const gateway = useReferenciasGateway();
  const consulta = useInfiniteQuery({
    queryKey: chavesDeReferencias.lista({ ...filtro, modo: "completo" }),
    queryFn: ({ pageParam }) =>
      gateway.listar({
        ...filtro,
        limite: LIMITE_POR_PAGINA,
        ...(pageParam !== undefined ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  useEffect(() => {
    if (
      consulta.hasNextPage &&
      !consulta.isFetchingNextPage &&
      !consulta.isFetching &&
      !consulta.isError
    ) {
      void consulta.fetchNextPage();
    }
  }, [
    consulta.hasNextPage,
    consulta.isFetchingNextPage,
    consulta.isFetching,
    consulta.isError,
    consulta.fetchNextPage,
  ]);

  return consulta;
}

export function useListarColecoes() {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.colecoes,
    queryFn: () => gateway.listarColecoes(),
  });
}

/**
 * Mostra a ultima lista na hora e pede a versao atual do Instagram.
 * Sem isso, o cache de 24h esconde colecao nova e nao da para marcar.
 */
export function useListarColecoesDisponiveis(habilitado = true) {
  const gateway = useReferenciasGateway();
  const cache = habilitado ? lerCacheColecoesDisponiveis() : null;

  return useQuery({
    queryKey: chavesDeReferencias.disponiveis,
    queryFn: async () => {
      const dados = await gateway.listarColecoesDisponiveis(true);
      gravarCacheColecoesDisponiveis(dados);
      return dados;
    },
    staleTime: 0,
    gcTime: 1000 * 60 * 60,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    enabled: habilitado,
    ...(cache
      ? {
          initialData: cache.dados,
          initialDataUpdatedAt: cache.atualizadoEm,
          placeholderData: cache.dados,
        }
      : {}),
  });
}

export function useSalvarColecoesSelecionadas() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (ids: readonly string[]) =>
      gateway.salvarColecoesSelecionadas(ids),
    onSuccess: async (resposta) => {
      await cliente.cancelQueries({
        queryKey: chavesDeReferencias.disponiveis,
      });
      const selecionadas = new Set(resposta.ids);
      cliente.setQueryData<readonly ColecaoDisponivel[]>(
        chavesDeReferencias.disponiveis,
        (atual) => {
          if (!atual) return atual;
          const proximo = atual.map((colecao) => ({
            ...colecao,
            selecionada: selecionadas.has(colecao.id),
          }));
          gravarCacheColecoesDisponiveis(proximo);
          return proximo;
        },
      );
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.colecoes,
      });
    },
  });
}

export function useListarContasSeguidas(habilitado = true) {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.contasSeguidas,
    queryFn: () => gateway.listarContasSeguidas({ forcar: true }),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    enabled: habilitado,
  });
}

export function useAtualizarContasSeguidas() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.listarContasSeguidas({ forcar: true }),
    onSuccess: (dados) => {
      cliente.setQueryData(chavesDeReferencias.contasSeguidas, dados);
    },
  });
}

export function useSalvarContasStoriesSelecionadas() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (ids: readonly string[]) =>
      gateway.salvarContasStoriesSelecionadas(ids),
    onSuccess: async (resposta) => {
      await cliente.cancelQueries({
        queryKey: chavesDeReferencias.contasSeguidas,
      });
      const selecionadas = new Set(resposta.ids);
      cliente.setQueryData<readonly ContaSeguida[]>(
        chavesDeReferencias.contasSeguidas,
        (atual) => {
          if (!atual) return atual;
          return atual.map((conta) => ({
            ...conta,
            selecionada: selecionadas.has(conta.id),
          }));
        },
      );
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.storiesContas,
      });
    },
  });
}

export function useListarContasStoriesMonitoradas() {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.storiesContas,
    queryFn: () => gateway.listarContasStoriesMonitoradas(),
    staleTime: 30_000,
  });
}

export function useListarStoriesInfinito(filtro: FiltroDeStories) {
  const gateway = useReferenciasGateway();
  const chaveFiltro = {
    ...(filtro.contaIds ? { contaIds: [...filtro.contaIds].sort() } : {}),
    ...(filtro.q ? { q: filtro.q } : {}),
    ...(filtro.mediaType ? { mediaType: filtro.mediaType } : {}),
    ...(filtro.ordenar ? { ordenar: filtro.ordenar } : {}),
    ...(filtro.soFavoritos ? { soFavoritos: true } : {}),
  };
  return useInfiniteQuery({
    queryKey: chavesDeReferencias.stories(chaveFiltro),
    queryFn: ({ pageParam }) =>
      gateway.listarStories({
        ...filtro,
        limite: filtro.limite ?? LIMITE_POR_PAGINA,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
    enabled: Boolean(filtro.contaIds && filtro.contaIds.length > 0),
  });
}

export function useAtualizarFlagsPost() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entrada: {
      readonly id: string;
      readonly favorito?: boolean;
      readonly oculto?: boolean;
    }) => gateway.atualizarFlagsPost(entrada),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeReferencias.todas });
    },
  });
}

export function useAtualizarFlagsStory() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entrada: {
      readonly id: string;
      readonly favorito?: boolean;
      readonly oculto?: boolean;
    }) => gateway.atualizarFlagsStory(entrada),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeReferencias.todas });
    },
  });
}

export function useStatusSincronizacaoStories() {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.storiesSincronizacao,
    queryFn: () => gateway.statusSincronizacaoStories(),
    // Enquanto roda, poll rapido pra barra de progresso. Parado, poll lento
    // so pra refletir o cron em background (sem isso, a hora da ultima
    // atualizacao so mudaria com F5).
    refetchInterval: (consulta) =>
      consulta.state.data?.running === true ? 2_000 : 60_000,
  });
}

export function useSincronizarStories() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.sincronizarStories(),
    onSuccess: () => {
      void cliente.invalidateQueries({
        queryKey: ["referencias", "stories"],
      });
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.storiesSincronizacao,
      });
    },
  });
}

export function useStatusSincronizacao() {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.sincronizacao,
    queryFn: () => gateway.statusSincronizacao(),
    refetchInterval: (consulta) =>
      consulta.state.data?.running === true ? 2_000 : false,
  });
}

export function useSincronizar() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (modo: ModoSincronizacaoReferencias = "completo") =>
      gateway.sincronizar(modo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeReferencias.todas });
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.sincronizacao,
      });
    },
  });
}

/**
 * Recarrega lista e coleções do banco sem iniciar sync com o Instagram.
 * Útil enquanto o sync roda em background e novos posts já foram persistidos.
 */
export function useAtualizarBiblioteca() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await cliente.refetchQueries({ queryKey: chavesDeReferencias.todas });
    },
  });
}

export function useStatusSessaoInstagram() {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.instagramSessao,
    queryFn: () => gateway.statusSessaoInstagram(),
    refetchInterval: 60_000,
  });
}

export function useStatusIntegracoes() {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.integracoes,
    queryFn: () => gateway.statusIntegracoes(),
  });
}

export function useAtualizarClickUp() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entrada: {
      readonly token?: string;
      readonly listId?: string;
    }) => gateway.atualizarClickUp(entrada),
    onSuccess: (dados) => {
      cliente.setQueryData(chavesDeReferencias.integracoes, dados);
    },
  });
}

export function useValidarSessaoInstagram() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.validarSessaoInstagram(),
    onSuccess: (dados) => {
      cliente.setQueryData(chavesDeReferencias.instagramSessao, dados);
    },
  });
}

export function useInvalidarSessaoInstagram() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.invalidarSessaoInstagram(),
    onSuccess: () => {
      limparCacheColecoesDisponiveis();
      cliente.setQueryData(chavesDeReferencias.disponiveis, []);
      cliente.setQueryData(chavesDeReferencias.contasSeguidas, []);
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.instagramSessao,
      });
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.disponiveis,
      });
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.contasSeguidas,
      });
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.colecoes,
      });
    },
  });
}

export function useConectarInstagram() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entrada: { username: string; password: string }) =>
      gateway.conectarInstagram(entrada),
    onSuccess: (resposta) => {
      if (resposta.status === "conectada") {
        cliente.setQueryData(
          chavesDeReferencias.instagramSessao,
          resposta.sessao,
        );
      }
    },
  });
}

export function useImportarSessaoInstagram() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (sessao: Record<string, unknown>) =>
      gateway.importarSessaoInstagram(sessao),
    onSuccess: (sessao) => {
      cliente.setQueryData(chavesDeReferencias.instagramSessao, sessao);
    },
  });
}

export function useConfirmar2faInstagram() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entrada: { desafioId: string; codigo: string }) =>
      gateway.confirmar2faInstagram(entrada),
    onSuccess: (resposta) => {
      if (resposta.status === "conectada") {
        cliente.setQueryData(
          chavesDeReferencias.instagramSessao,
          resposta.sessao,
        );
      }
    },
  });
}

export function useAtualizarColecoesDoInstagram() {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.listarColecoesDisponiveis(true),
    onSuccess: (dados) => {
      gravarCacheColecoesDisponiveis(dados);
      cliente.setQueryData(chavesDeReferencias.disponiveis, dados);
    },
  });
}

export function useListarComentariosReferencia(postId: string) {
  const gateway = useReferenciasGateway();
  return useQuery({
    queryKey: chavesDeReferencias.comentarios(postId),
    queryFn: () => gateway.listarComentarios(postId),
    enabled: Boolean(postId),
  });
}

export function useCriarComentarioReferencia(postId: string) {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entrada: { texto: string; autorNome: string }) =>
      gateway.criarComentario(postId, entrada),
    onSuccess: () => {
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.comentarios(postId),
      });
    },
  });
}

export function useAtualizarComentarioReferencia(postId: string) {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entrada: { comentarioId: string; texto: string }) =>
      gateway.atualizarComentario(
        postId,
        entrada.comentarioId,
        entrada.texto,
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.comentarios(postId),
      });
    },
  });
}

export function useExcluirComentarioReferencia(postId: string) {
  const gateway = useReferenciasGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (comentarioId: string) =>
      gateway.excluirComentario(postId, comentarioId),
    onSuccess: () => {
      void cliente.invalidateQueries({
        queryKey: chavesDeReferencias.comentarios(postId),
      });
    },
  });
}
